import { useEffect, useMemo, useState } from "react";
import InputSearch from "../../../../../components/inputs/inputSearch/inputSearch";
import Notification from "../../../../../components/notification/notification";
import { getApiInstance } from "../../../../../utils/api";
import CardTarifa from "../../../../../components/cardTable/cardTarifa";
import Pagination from "../../../../../components/pagination/pagination";
import "./comparativaTarifas.scss";
import { FiFilter } from "react-icons/fi";
import Button from "../../../../../components/button/button";
import Modal from "../../../../../components/modal/modal";
import InputDropdown from "../../../../../components/inputs/inputDropdown/inputDropdown";
import Accordion from "../../../../../components/accordion/accordion";
import Tag from "../../../../../components/tag/tag";
import { searchByKeywords } from "../../../../../utils/searchUtils";

const estadosIniciales = [
    { id: 1, label: "Pendiente de información" },
    { id: 2, label: "Informado" },
    { id: 3, label: "Proposición de nueva tarifa" },
    { id: 4, label: "Rechazada pendiente de revisión" },
    { id: 5, label: "Informado después de revisión" },
    { id: 6, label: "Aprobada Atlante" },
    { id: 7, label: "Aceptado" },
    { id: 8, label: "Rechazado" },
    { id: 9, label: "Desestimado" },
];

const estadoVariant = (estadoId) => {
    const variants = {
        1: "default",
        2: "info",
        3: "info",
        4: "warning",
        5: "info",
        6: "success",
        7: "success",
        8: "danger",
        9: "danger",
    };
    return variants[estadoId] || "default";
};

const transformData = (data) => {
    return data.map((item) => ({
        ...item,
        preciosEmpresas: item.empresas.map((empresa) => ({
            key: empresa.nombre || "Desconocida",
            value:
                item.preciosEmpresas.find(
                    (precio) => precio.key === empresa.codEmpresa
                )?.value || 0,
        })),
        comparativas: item.empresas.map((empresa) => ({
            key: empresa.nombre || "Desconocida",
            value: (() => {
                const v = item.comparativas.find(
                    (c) => c.key === empresa.codEmpresa
                )?.value;
                if (v == null) return null; // si no existe o es null
                if (v === 0) return 0; // si vale exactamente 0
                return Math.round(v * 100) / 100; // redondeo a 2 decimales
            })(),
        })),
        comentariosEmpresas: item.comentariosEmpresas.map((comentario) => ({
            key:
                item.empresas.find((emp) => emp.codEmpresa === comentario.key)
                    ?.nombre || "Desconocida",
            value: comentario.value,
        })),
        estadosEmpresas: item.estadosEmpresas.map((estado) => {
            const estadoInfo = estadosIniciales.find(
                (e) => e.id === estado.value
            ) || { label: "Sin estado" };
            return {
                key:
                    item.empresas.find((emp) => emp.codEmpresa === estado.key)
                        ?.nombre || "Desconocida",
                value: estadoInfo.label,
                variant: estadoVariant(estado.value),
                id: estado.value,
            };
        }),
    }));
};

export default function ComparativaTarifas({ user }) {
    const [searchTerm, setSearchTerm] = useState("");
    const [comparativas, setComparativas] = useState([]);
    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
    const [mueblesOptions, setMueblesOptions] = useState([]);
    const [filterMuebles, setFilterMuebles] = useState([]);
    const [filterNombreTarifa, setFilterNombreTarifa] = useState([]);
    const [filterEstados, setFilterEstados] = useState([]);
    const [filterCategoria, setFilterCategoria] = useState([]);
    const [filterSubcategoria, setFilterSubcategoria] = useState([]);
    const [tarifasOptions, setTarifasOptions] = useState([]);
    const [estadosOptions, setEstadosOptions] = useState(estadosIniciales);
    const [categorias, setCategorias] = useState([]);
    const [subCategorias, setSubcategorias] = useState([]);
    const [usedFilter, setUsedFilter] = useState();
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    useEffect(() => {
        const fetchData = async () => {
            const api = getApiInstance();
            try {
                const response = await api.get(
                    `/api/MueblesPrecios/Comparativa/${user.codEmpresa}`
                );
                if (response.data?.length > 0) {
                    const transformedData = transformData(response.data);
                    setComparativas(transformedData);
                }
            } catch (error) {
                console.error("Error al obtener los datos:", error);
            }
        };
        fetchData();
    }, [user.codEmpresa]);

    const filteredByFilters = useMemo(() => {
        return comparativas.filter((item) => {
            if (
                filterNombreTarifa.length > 0 &&
                !filterNombreTarifa.includes(item.nombreTarifa)
            ) {
                return false;
            }

            if (
                filterMuebles.length > 0 &&
                !filterMuebles.includes(item.nombreMueble)
            ) {
                return false;
            }

            if (filterEstados.length > 0) {
                const itemEstadosIds = item.estadosEmpresas.map((e) => e.id);
                const filterEstadosIds = filterEstados.map((e) => e.id);
                if (
                    !itemEstadosIds.some((id) => filterEstadosIds.includes(id))
                ) {
                    return false;
                }
            }

            if (filterCategoria.length > 0) {
                if (!filterCategoria.includes(item.familia)) {
                    return false;
                }
            }

            if (filterSubcategoria.length > 0) {
                // Ahora usamos la nueva propiedad nombreSubfamilia del backend
                if (!filterSubcategoria.includes(item.nombreSubfamilia)) {
                    return false;
                }
            }

            return true;
        });
    }, [
        comparativas,
        filterNombreTarifa,
        filterMuebles,
        filterEstados,
        filterCategoria,
        filterSubcategoria,
    ]);

    useEffect(() => {
        if (comparativas.length === 0) return;

        const getFilteredDataExcluding = (excludeFilter) => {
            return comparativas.filter((item) => {
                // Búsqueda por término
                if (searchTerm) {
                    const term = searchTerm.toLowerCase();
                    const matchesSearch =
                        (item.nombreMueble && item.nombreMueble.toLowerCase().includes(term)) ||
                        (item.nombreTarifa && item.nombreTarifa.toLowerCase().includes(term)) ||
                        (item.referencia && item.referencia.toLowerCase().includes(term));
                    if (!matchesSearch) return false;
                }

                // Aplicar todos los filtros EXCEPTO el que estamos excluyendo
                if (excludeFilter !== "mueble" && filterMuebles.length > 0) {
                    if (!filterMuebles.includes(item.nombreMueble))
                        return false;
                }

                if (
                    excludeFilter !== "nombreTarifa" &&
                    filterNombreTarifa.length > 0
                ) {
                    if (!filterNombreTarifa.includes(item.nombreTarifa))
                        return false;
                }

                if (excludeFilter !== "estado" && filterEstados.length > 0) {
                    const itemEstadosIds = item.estadosEmpresas.map(
                        (e) => e.id
                    );
                    const filterEstadosIds = filterEstados.map((e) => e.id);
                    if (
                        !itemEstadosIds.some((id) =>
                            filterEstadosIds.includes(id)
                        )
                    ) {
                        return false;
                    }
                }

                if (
                    excludeFilter !== "categoria" &&
                    filterCategoria.length > 0
                ) {
                    if (!filterCategoria.includes(item.familia)) return false;
                }

                if (
                    excludeFilter !== "subcategoria" &&
                    filterSubcategoria.length > 0
                ) {
                    if (!filterSubcategoria.includes(item.nombreSubfamilia))
                        return false;
                }

                return true;
            });
        };

        const dataForMuebles = getFilteredDataExcluding("mueble");
        const mueblesFiltrados = Array.from(
            new Set(dataForMuebles.map((item) => item.nombreMueble))
        ).map((mueble) => ({ label: mueble, value: mueble }));
        setMueblesOptions(mueblesFiltrados);

        const dataForTarifas = getFilteredDataExcluding("nombreTarifa");
        const tarifasFiltradas = Array.from(
            new Set(dataForTarifas.map((item) => item.nombreTarifa))
        ).map((tarifa) => ({ label: tarifa, value: tarifa }));
        setTarifasOptions(tarifasFiltradas);

        const dataForEstados = getFilteredDataExcluding("estado");
        const estadosIds = new Set();
        dataForEstados.forEach((item) => {
            item.estadosEmpresas.forEach((estado) => {
                estadosIds.add(estado.id);
            });
        });
        setEstadosOptions(
            estadosIniciales.filter((estado) => estadosIds.has(estado.id))
        );

        const dataForCategorias = getFilteredDataExcluding("categoria");
        const allCategorias = dataForCategorias
            .map((item) => item.familia)
            .filter(
                (value, index, self) =>
                    value &&
                    value !== null &&
                    value !== undefined &&
                    self.indexOf(value) === index
            )
            .sort(); // Ordenar alfabéticamente para consistencia
        setCategorias(allCategorias);

        const dataForSubcategorias = getFilteredDataExcluding("subcategoria");
        const allSubcategorias = dataForSubcategorias
            .map((item) => item.nombreSubfamilia)
            .filter(
                (value, index, self) =>
                    value &&
                    value !== null &&
                    value !== undefined &&
                    self.indexOf(value) === index
            )
            .sort(); // Ordenar alfabéticamente para consistencia
        setSubcategorias(allSubcategorias);
    }, [
        searchTerm,
        filterMuebles,
        filterNombreTarifa,
        filterEstados,
        filterCategoria,
        filterSubcategoria,
        comparativas,
    ]);

    // useEffect separado solo para la carga inicial de categorías y subcategorías
    useEffect(() => {
        if (
            comparativas.length > 0 &&
            categorias.length === 0 &&
            subCategorias.length === 0
        ) {
            const allCategorias = comparativas
                .map((item) => item.familia)
                .filter(
                    (value, index, self) =>
                        value &&
                        value !== null &&
                        value !== undefined &&
                        self.indexOf(value) === index
                );

            const allSubcategorias = comparativas
                .map((item) => item.nombreSubfamilia)
                .filter(
                    (value, index, self) =>
                        value &&
                        value !== null &&
                        value !== undefined &&
                        self.indexOf(value) === index
                );

            setCategorias(allCategorias);
            setSubcategorias(allSubcategorias);
        }
    }, [comparativas, categorias.length, subCategorias.length]);

const filteredComparativas = useMemo(() => {
    return searchByKeywords(
        filteredByFilters,
        searchTerm,
        ["nombreMueble", "nombreTarifa", "referencia"]
    );
}, [filteredByFilters, searchTerm]);


    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredComparativas.slice(
        indexOfFirstItem,
        indexOfLastItem
    );

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const handleSelectedMueble = (selected) => {
        setFilterMuebles(selected);
        setCurrentPage(1);
    };

    const handleSelectNombreTarifa = (selected) => {
        setFilterNombreTarifa(selected);
        setCurrentPage(1);
    };

    const handleSelectEstado = (estado) => {
        setFilterEstados((prev) =>
            prev.some((e) => e.id === estado.id)
                ? prev.filter((e) => e.id !== estado.id)
                : [...prev, estado]
        );
        setCurrentPage(1);
    };

    const handleSelectCategoria = (categoriaName) => {
        setFilterCategoria((prevSelected) => {
            if (prevSelected.includes(categoriaName)) {
                return prevSelected.filter(
                    (categoria) => categoria !== categoriaName
                );
            } else {
                return [...prevSelected, categoriaName];
            }
        });
        setCurrentPage(1);
    };

    const handleSelectSubcategoria = (subcategoriaName) => {
        setFilterSubcategoria((prevSelected) => {
            if (prevSelected.includes(subcategoriaName)) {
                return prevSelected.filter(
                    (subcategoria) => subcategoria !== subcategoriaName
                );
            } else {
                return [...prevSelected, subcategoriaName];
            }
        });
        setCurrentPage(1);
    };

    const handleCleanFilter = () => {
        setFilterMuebles([]);
        setFilterNombreTarifa([]);
        setFilterEstados([]);
        setFilterCategoria([]);
        setFilterSubcategoria([]);
        setCurrentPage(1);
    };

    const applyFiltersAndClose = () => {
        setIsFiltersModalOpen(false);
    };

    return (
        <section className="dashboard-usuarios">
            <div className="dashboard-usuarios__content__table-header">
                <InputSearch
                    context="tableSearch"
                    placeholder="Buscar..."
                    onSearch={setSearchTerm}
                />
                <div className="dashboard-productos__content__table-header__buttons">
                    <Button
                        variant="outline"
                        label="Filtros"
                        icon={<FiFilter />}
                        onClick={() => setIsFiltersModalOpen(true)}
                    />
                </div>
            </div>

            <div className="comparativa-tarifas__cards">
                {currentItems.map((mueble, index) => (
                    <CardTarifa
                        key={index}
                        imagen={mueble.rutaImagen}
                        titulo={mueble.nombreMueble}
                        referencia={mueble.referencia}
                        categoria={mueble.familia}
                        proveedores={mueble.empresas.map(
                            (empresa) => empresa.nombre
                        )}
                        precios={mueble.preciosEmpresas.map((precio) =>
                            precio.value !== 0
                                ? `${precio.value} ${mueble.simbolo}`
                                : "-"
                        )}
                        comparativas={mueble.comparativas.map((comparativa) => {
                            const value =
                                typeof comparativa.value === "number" &&
                                !isNaN(comparativa.value)
                                    ? comparativa.value
                                    : null;
                            return value !== null ? `${value} %` : "- %";
                        })}
                        tarifasAprobadas={mueble.empresas.map((empresa) =>
                            empresa.codEmpresa === mueble.codEmpresaAprobada
                                ? 1
                                : 0
                        )}
                        comentarios={mueble.comentariosEmpresas.map(
                            (comentario) =>
                                `${comentario.key}: ${comentario.value}` || "-"
                        )}
                        estados={mueble.estadosEmpresas.map((estado) => ({
                            key: estado.key,
                            value: estado.value, // Texto del estado
                            variant: estado.variant,
                        }))}
                        medida={mueble.medida}
                        nombreTarifa={mueble.nombreTarifa}
                    />
                ))}
            </div>

            {filteredComparativas.length > itemsPerPage && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={Math.ceil(
                        filteredComparativas.length / itemsPerPage
                    )}
                    onPageChange={handlePageChange}
                />
            )}

            <Notification />

            <Modal
                title="Filtros"
                isOpen={isFiltersModalOpen}
                onClose={() => setIsFiltersModalOpen(false)}
                onAccept={applyFiltersAndClose}
                outlineButtonLabel="Limpiar"
                primaryButtonLabel={`Ver resultados (${filteredComparativas.length})`}
                onCancel={handleCleanFilter}
            >
                <div className="modal-inputs">
                    <div className="section-header__modal__section">
                        <InputDropdown
                            label="Mueble"
                            options={mueblesOptions}
                            onSelect={handleSelectedMueble}
                            withCheckboxes={true}
                            hasSearch={true}
                            selectedValues={filterMuebles}
                        />
                    </div>

                    <div className="section-header__modal__section">
                        <InputDropdown
                            label="Nombre Tarifa"
                            options={tarifasOptions}
                            onSelect={handleSelectNombreTarifa}
                            withCheckboxes={true}
                            hasSearch={true}
                            selectedValues={filterNombreTarifa}
                        />
                    </div>

                    <div className="section-header__modal__section">
                        <Accordion title="Categoría">
                            <ul className="section-header__modal__section__content">
                                {categorias.map((categoria, index) => (
                                    <Tag
                                        key={index}
                                        label={categoria}
                                        isSelected={filterCategoria.includes(
                                            categoria
                                        )}
                                        onSelect={handleSelectCategoria}
                                    />
                                ))}
                            </ul>
                        </Accordion>
                    </div>

                    <div className="section-header__modal__section">
                        <Accordion title="Imagen">
                            <ul className="section-header__modal__section__content">
                                {subCategorias.map((subcategoria, index) => (
                                    <Tag
                                        key={index}
                                        label={subcategoria}
                                        isSelected={filterSubcategoria.includes(
                                            subcategoria
                                        )}
                                        onSelect={handleSelectSubcategoria}
                                    />
                                ))}
                            </ul>
                        </Accordion>
                    </div>

                    <div className="section-header__modal__section">
                        <Accordion title="Estado">
                            <ul className="section-header__modal__section__content">
                                {estadosOptions.map((estado) => (
                                    <Tag
                                        key={estado.id}
                                        label={estado.label}
                                        isSelected={filterEstados.some(
                                            (e) => e.id === estado.id
                                        )}
                                        onSelect={() =>
                                            handleSelectEstado(estado)
                                        }
                                        variant={estadoVariant(estado.id)}
                                    />
                                ))}
                            </ul>
                        </Accordion>
                    </div>
                </div>
            </Modal>
        </section>
    );
}
